import {Box, Typography} from '@mui/material';
import AurIconButton from '../common/aur-button/AurIconButton';
import AurIcon, {IconNames} from '../common/aur-icon/AurIcon';
import {t} from 'i18next';
import TeamMemberList from './TeamMemberList';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {downloadConciergeReportFile} from '../../api/ReportingManagement';
import {
  downloadFile,
  getFileExtension,
  getFilenameFromHeaders,
  printFile,
} from '../../utils/utils';
import {defaultReportFileName} from '../../constants/constants';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {useEffect, useState} from 'react';
import {
  RowData,
  UserData,
  UserListData,
} from '../../types/concierge-reports/conciergeReportsType';
import {AxiosError} from 'axios';
import {useAnalytics} from '../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../constants/analyticsConstants';

interface ReportDetailsProps {
  selectedReport: RowData;
}

const ReportDetails = (props: ReportDetailsProps) => {
  const {selectedReport} = props;

  const {trackGAEvent} = useAnalytics();

  const clientRole = useSelector(
    (state: RootState) => state.conciergeReport.isClient
  );

  const [updatedTeamMember, setUpdatedTeamMember] = useState<UserData[]>();

  const {showToast} = useToast();

  useEffect(() => {
    if (!clientRole && selectedReport) {
      const userListData = selectedReport?.userList?.map(
        (user: UserListData) => {
          const isOwner = user.userId === selectedReport?.createdBy;
          return {
            userId: user.userId,
            name: `${user.firstName} ${user.lastName}`,
            isOwner,
          };
        }
      );
      const owner = userListData.find((user: UserData) => user.isOwner);
      const otherUsers = userListData.filter((user: UserData) => !user.isOwner);
      setUpdatedTeamMember(owner ? [owner, ...otherUsers] : otherUsers);
    }
  }, [clientRole, selectedReport]);

  const downloadPrintHandler = async (value: string) => {
    try {
      const reportId = selectedReport.id;
      if (reportId) {
        const response = await downloadConciergeReportFile(reportId);
        const resHeaders = response.headers;
        const fileName =
          getFilenameFromHeaders(resHeaders['content-disposition']) ||
          defaultReportFileName;
        const resdata = response.data;
        trackGAEvent({
          eventName:
            value === 'print'
              ? GA_EVENT_NAMES.PRINT
              : GA_EVENT_NAMES.FILE_DOWNLOAD,
          file_extension: getFileExtension(fileName),
          click: {
            name: `${t('gaTracking.reports.detailsView')} ${value}`,
            type: GA_CLICK_TYPES.BUTTON,
            region: GA_REGIONS.BODY,
            action: t(`gaTracking.reports.${value}Report`),
          },
          subPage: {
            subpage_name: t('gaTracking.reports.customReportDetails'),
            subpage_type: GA_SUBPAGE_TYPE.DRAWER,
          },
        });

        if (
          value === 'print' &&
          resHeaders['content-type'] === 'application/pdf'
        )
          printFile(resdata);
        else downloadFile(resdata, fileName);
      }
    } catch (error) {
      const err = error as AxiosError;
      showToast(err.message, 'error', undefined, 'center', undefined, {
        code: err?.code,
      });
    }
  };
  return (
    <>
      <Box className="report-data-container" role="main">
        <Box className="panel-title">
          <Box className="report-name">
            <Typography className={' drawer-header-title'}>
              {selectedReport.name}
            </Typography>
          </Box>

          <Box className="report-icons">
            <AurIconButton
              size="big"
              ariaLabel={t('conciergeReports.buttonsLabels.download')}
              shape
              icon={<AurIcon icon={IconNames.Download} />}
              onClick={() => downloadPrintHandler('download')}
            />
            <AurIconButton
              size="big"
              shape
              ariaLabel={t('conciergeReports.buttonsLabels.print')}
              icon={<AurIcon icon={IconNames.Print} />}
              disabled={getFileExtension(selectedReport.name) !== 'pdf'}
              onClick={() => downloadPrintHandler('print')}
            />
          </Box>
        </Box>
        <Box className="report-panel-client">
          <Typography className="typo-text label2_size mist-700 client-header">
            {t('conciergeReports.detailsPanel.client')}&nbsp;.&nbsp;
            {t('conciergeReports.detailsPanel.project')}
          </Typography>
          <Typography className="typo-text body1_size client-body">
            {selectedReport.client}&nbsp;.&nbsp;
            {selectedReport.project}&nbsp;|&nbsp;
            {selectedReport.projectCode}
          </Typography>
        </Box>
        <Box className="report-panel-desc">
          <Typography className="typo-text label2_size mist-700 client-header">
            {t('conciergeReports.detailsPanel.description')}
          </Typography>
          <Typography className="typo-text body1_size client-body">
            {selectedReport.description}
          </Typography>
        </Box>
      </Box>
      {!clientRole ? (
        <Box className="report-panel-team-member-list">
          <TeamMemberList
            userList={updatedTeamMember ?? []}
            client={selectedReport.client}
          />
        </Box>
      ) : null}
    </>
  );
};

export default ReportDetails;
